import React, { useState, useRef, useCallback, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;

function formatTime(secs) {
  if (!secs || !isFinite(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const styles = {
  container: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', borderRadius: 12,
    background: 'rgba(255,255,255,0.06)',
    minWidth: 200, maxWidth: 300,
  },
  playBtn: {
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
  },
  waveform: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  bars: {
    display: 'flex', alignItems: 'center', gap: 2, height: 24, cursor: 'pointer',
    position: 'relative',
  },
  bar: {
    width: 3, borderRadius: 2, background: 'rgba(255,255,255,0.25)',
    transition: 'background .15s',
  },
  barPlayed: { background: '#667eea' },
  time: { fontSize: 11, color: 'rgba(255,255,255,0.5)' },
  loading: {
    fontSize: 12, color: 'rgba(255,255,255,0.5)',
    padding: '8px 12px',
  },
};

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
  </svg>
);

const NUM_BARS = 28;

function generateBars(seed) {
  const bars = [];
  let h = 0.5;
  for (let i = 0; i < NUM_BARS; i++) {
    h = 0.3 + Math.abs(Math.sin(seed + i * 0.7)) * 0.7;
    bars.push(h);
  }
  return bars;
}

export default function AudioBubble({ url, sender }) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);
  const barsRef = useRef(generateBars(url ? url.length : 42));

  const resolvedUrl = url?.startsWith('/') ? `${API_BASE}${url}` : url;

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = resolvedUrl;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      setLoading(false);
    });
    audio.addEventListener('canplay', () => setLoading(false));
    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('ended', () => { setPlaying(false); setCurrentTime(0); });
    audio.addEventListener('error', () => setLoading(false));

    audioRef.current = audio;
    return () => { audio.pause(); audio.src = ''; };
  }, [resolvedUrl]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [playing]);

  const handleSeek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * duration;
    setCurrentTime(audio.currentTime);
  }, [duration]);

  const progress = duration > 0 ? currentTime / duration : 0;

  if (loading) {
    return <div style={styles.loading}>Loading audio...</div>;
  }

  return (
    <div style={styles.container}>
      <button style={styles.playBtn} onClick={togglePlay}>
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <div style={styles.waveform}>
        <div style={styles.bars} onClick={handleSeek}>
          {barsRef.current.map((h, i) => {
            const played = i / NUM_BARS <= progress;
            return (
              <div
                key={i}
                style={{
                  ...styles.bar,
                  ...(played ? styles.barPlayed : {}),
                  height: `${h * 100}%`,
                }}
              />
            );
          })}
        </div>
        <span style={styles.time}>
          {playing ? formatTime(currentTime) : formatTime(duration)}
        </span>
      </div>
    </div>
  );
}
